import { KeycloakService } from 'keycloak-angular';

/**
 * Attache le Bearer Keycloak à toute requête fetch() ciblant /media/ (protégé
 * par authentification côté backend, cf. core/urls.py::ProtectedMediaView).
 *
 * Nécessaire en complément d'AuthInterceptor (qui ne couvre que HttpClient) :
 * pdf.js (rc-sign, pdf-canvas-viewer) et certains appels fetch() bruts
 * chargent des documents/images issus de /media/ sans passer par HttpClient.
 * Patcher le fetch global une seule fois ici évite de dupliquer la logique
 * (récupération du token, garde isLoggedIn) dans chaque composant appelant —
 * tout /media/ chargé n'importe où dans l'app est couvert automatiquement.
 */
export function installMediaFetchAuth(keycloakService: KeycloakService): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

    if (!url.includes('/media/') || !keycloakService.isLoggedIn()) {
      return originalFetch(input, init);
    }

    try {
      const token = await keycloakService.getToken();
      if (!token) {
        return originalFetch(input, init);
      }
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
      headers.set('Authorization', `Bearer ${token}`);
      return originalFetch(input, { ...init, headers });
    } catch {
      // Pas de session exploitable : on laisse passer la requête telle quelle
      // (utile pour les liens publics token-scopés, qui n'en ont pas besoin).
      return originalFetch(input, init);
    }
  };
}

let bridgeInFlight: Promise<void> | null = null;

/**
 * Pont Keycloak -> cookie Simple-JWT (voir KeycloakCookieBridgeView côté
 * backend) : un <img src="/media/..."> natif ne peut porter aucun en-tête,
 * donc le fetch patché ci-dessus ne suffit pas pour lui — il lui faut le
 * cookie que CookiesAuthentication sait lire.
 *
 * Le cookie contient un JWT Simple-JWT dont la durée de vie
 * (SIMPLE_JWT.ACCESS_TOKEN_LIFETIME, 5 min) est bien plus courte que son
 * max_age côté navigateur (REFRESH_TOKEN_LIFETIME, 1 jour) : il faut donc le
 * réémettre périodiquement, pas seulement une fois au démarrage. Appelé par
 * main.ts (bootstrap) ET par TokenRefreshService (à chaque refresh du token
 * Keycloak, ~toutes les 4 min) pour rester synchronisé avec la session.
 */
export async function bridgeKeycloakToMediaCookie(keycloakService: KeycloakService): Promise<void> {
  if (!keycloakService.isLoggedIn()) {
    return;
  }
  if (bridgeInFlight) {
    return bridgeInFlight;
  }
  bridgeInFlight = (async () => {
    try {
      const token = await keycloakService.getToken();
      await fetch('/api/token/keycloak-cookie/', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
    } catch (err) {
      console.warn('[bridgeKeycloakToMediaCookie] Échec de la pose du cookie /media/:', err);
    } finally {
      bridgeInFlight = null;
    }
  })();
  return bridgeInFlight;
}
